import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { ProjectCard } from '../components/projects/ProjectCard';
import { ProjectFilters } from '../components/projects/ProjectFilters';
import { Card } from '../components/ui/Card';
import { StatusPill } from '../components/ui/StatusPill';
import { MapPinIcon, BanknoteIcon, CalendarIcon, StarIcon } from 'lucide-react';
import { projects } from '../data/projects';
export function Projects() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('All Sectors');
  const [statusFilter, setStatusFilter] = useState('all');
  const sectors = useMemo(() => Array.from(new Set(projects.map(p => p.sector))).sort(), []);
  const featuredProject = projects.find(p => p.featured);
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) || project.description.toLowerCase().includes(searchQuery.toLowerCase()) || project.countries.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSector = sectorFilter === 'All Sectors' || project.sector === sectorFilter;
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesSector && matchesStatus;
  });
  return <div data-id="element-807" data-mp="2qed4dy1rwxz2">
      <PageHero title="Our Projects" subtitle="Transformative initiatives advancing sustainable development across Africa through global partnerships" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Projects'
    }]} data-id="element-808" data-mp="2v7660d1y3q6x" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-809" data-mp="27imwxvb2n9ds">
        {featuredProject && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-12" data-id="element-810" data-mp="2ch81u819qh8k">
            <div className="flex items-center gap-2 mb-4" data-id="element-811" data-mp="1jlgxt59g71gk">
              <StarIcon size={20} className="text-au-gold fill-au-gold" data-id="element-812" data-mp="1jjxe14ol28a1" />
              <h2 className="text-2xl font-serif text-charcoal" data-id="element-813" data-mp="1ep08w8ufcj2f">
                Featured Project
              </h2>
            </div>

            <Card className="border-2 border-au-green overflow-hidden p-0" data-id="element-814" data-mp="27buoosgaqu9k">
              <div className="grid md:grid-cols-2" data-id="element-815" data-mp="279rpbht97os6">
                <div className="bg-au-green p-8 md:p-10 text-white flex flex-col justify-center" data-id="element-816" data-mp="22byj4oryaurw">
                  <span className="inline-block bg-au-gold text-charcoal text-xs font-bold px-3 py-1 rounded-full mb-4 self-start" data-id="element-817" data-mp="1xegdp8d56xon">
                    {featuredProject.sector.toUpperCase()}
                  </span>
                  <h3 className="text-3xl md:text-4xl font-serif mb-4 leading-tight" data-id="element-818" data-mp="lxqck3nksw1w">
                    {featuredProject.title}
                  </h3>
                  <div className="w-16 h-1 bg-au-gold mb-4" data-id="element-819" data-mp="32ky2qfyyxb7s"></div>
                  <p className="text-au-green-light text-lg leading-relaxed" data-id="element-820" data-mp="32l7qry0gd8au">
                    {featuredProject.description}
                  </p>
                </div>

                <div className="p-8 md:p-10 flex flex-col justify-center bg-white" data-id="element-821" data-mp="3cbsw04amtb2q">
                  <div className="mb-6" data-id="element-822" data-mp="3ham056413pq8">
                    <StatusPill status={featuredProject.status} data-id="element-823" data-mp="3h9kn4w3gct5v">
                      {featuredProject.status.toUpperCase()}
                    </StatusPill>
                  </div>

                  <div className="space-y-5" data-id="element-824" data-mp="3qb240goooyvm">
                    <div className="flex items-start gap-3" data-id="element-825" data-mp="3v9dd2p75n5cs">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-826" data-mp="3v8u623j1m1dj">
                        <MapPinIcon size={20} className="text-au-green" data-id="element-827" data-mp="20vktg2v79zr2" />
                      </div>
                      <div data-id="element-828" data-mp="3rcppxr4vu9zk">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-829" data-mp="3rbwsjgrtcwu3">
                          Countries
                        </div>
                        <div className="font-medium text-charcoal" data-id="element-830" data-mp="3mdd5te5ebh0o">
                          {featuredProject.countries.join(', ')}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-831" data-mp="4amjhhmgia2">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-832" data-mp="38mzz1f48q8cx">
                        <BanknoteIcon size={20} className="text-au-green" data-id="element-833" data-mp="33l0u40sur3dv" />
                      </div>
                      <div data-id="element-834" data-mp="3hgmsgykz6ziy">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-835" data-mp="3co1y9vy5qkew">
                          Total Budget
                        </div>
                        <div className="font-bold text-au-green text-lg" data-id="element-836" data-mp="2owwjlk3ydy66">
                          {featuredProject.budget}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-837" data-mp="2ar4eimbihxup">
                      <div className="w-10 h-10 rounded-full bg-au-green-light flex items-center justify-center flex-shrink-0" data-id="element-838" data-mp="2fjxkfdipvc8d">
                        <CalendarIcon size={20} className="text-au-green" data-id="element-839" data-mp="20vu8lnhzt6bn" />
                      </div>
                      <div data-id="element-840" data-mp="25u46w6z0kn1h">
                        <div className="text-sm text-gray-600 mb-1" data-id="element-841" data-mp="25r9mgfg03282">
                          Timeline
                        </div>
                        <div className="font-medium text-charcoal" data-id="element-842" data-mp="2yit9kafpx0fc">
                          {new Date(featuredProject.startDate).toLocaleDateString('en-GB', {
                        month: 'short',
                        year: 'numeric'
                      })}
                          {featuredProject.endDate && ` – ${new Date(featuredProject.endDate).toLocaleDateString('en-GB', {
                        month: 'short',
                        year: 'numeric'
                      })}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>}

        <div className="mb-8" data-id="element-843" data-mp="310va01achtw5">
          <h2 className="text-2xl font-serif text-charcoal mb-2" data-id="element-844" data-mp="2w3v71z8sl799">
            All Projects
          </h2>
          <div className="w-16 h-1 bg-au-gold" data-id="element-845" data-mp="2s1rq4v3ctagl"></div>
        </div>

        <ProjectFilters searchQuery={searchQuery} setSearchQuery={setSearchQuery} sectorFilter={sectorFilter} setSectorFilter={setSectorFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter} sectors={sectors} data-id="element-846" data-mp="2n2x2nfufyxq5" />

        <div className="mb-6" data-id="element-847" data-mp="gcqiotxjgqrt">
          <p className="text-gray-600" data-id="element-848" data-mp="gdigmbelz0l9">
            Showing{' '}
            <span className="font-bold text-charcoal" data-id="element-849" data-mp="nw9igbv04yn3">
              {filteredProjects.length}
            </span>{' '}
            project{filteredProjects.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-850" data-mp="xl9k630w284z">
          {filteredProjects.map((project, index) => <ProjectCard key={project.id} project={project} index={index} data-id="element-851" data-mp="12july6vsgzvj" data-mpi={index} />)}
        </div>

        {filteredProjects.length === 0 && <div className="text-center py-16 bg-white rounded-lg border border-gray-border" data-id="element-852" data-mp="4zi50j255dqy">
            <p className="text-gray-600 text-lg mb-2" data-id="element-853" data-mp="9yb95kvjfseg">
              No projects match your filters.
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSectorFilter('All Sectors');
          setStatusFilter('all');
        }} className="text-au-green font-medium hover:underline" data-id="element-854" data-mp="juejvf4ggzd0">
              Clear filters
            </button>
          </div>}
      </div>
    </div>;
}